'use-client';
import { useScroll } from '../../../../common/menu/Scroll/useScroll';
import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import type { IMenuContentProps, LayoutClassType } from '../../Menu.types';
import { MenuItem } from '../../viewer/components/MenuItem/MenuItem';
import classes from './style/MenuContent.module.scss';
import { getCurrentMenuItem } from '../../../../common/menu/getCurrentMenuItem';
import { ScrollControls } from '../../viewer/components/ScrollControls/ScrollControls';
import {
  scrollToMenuItem,
  rootLevelMenuItemSelector,
} from '../../../../common/menu/Scroll/utils';
import { useResizeObserver } from '@wix/thunderbolt-elements/providers/useResizeObserver';
import { semanticClassNames as menuSemanticClassNames } from '../../Menu.semanticClassNames';
import shmSemanticClassNames from '../../../StylableHorizontalMenu/StylableHorizontalMenu.semanticClassNames';
import { useMenuContext } from '../../viewer/MenuContext';
import { DataHooks } from '../../constants';
import { MenuParts } from '../../viewer/constants';

const MenuContent: React.FC<IMenuContentProps> = props => {
  const {
    id,
    className,
    itemClassName,
    dropdownItemClassName,
    dropdownSubItemClassName,
    directionClassName,
    direction,
    submenuDirection,
    onItemMouseIn,
    onItemMouseOut,
    onItemClick,
    onItemDblClick,
    HorizontalDropdown,
    VerticalDropdown,
    withLogicalLayout = false,
  } = props;

  const {
    items,
    partToPreviewStateMap,
    currentUrl,
    activeAnchor,
    translations,
    customClassNames = [],
    getAnimationPackage,
    navAriaLabel,
    a11yProps,
  } = useMenuContext();

  const layoutClassType: LayoutClassType = withLogicalLayout
    ? 'logicalLayout'
    : 'physicalLayout';

  const rootClassName = classNames(
    classes.root,
    !direction && (directionClassName ?? classes.rootDirection),
    classes[layoutClassType],
    formatClassNames(shmSemanticClassNames.root),
    formatClassNames(menuSemanticClassNames.root, ...customClassNames),
  );

  const menuContainerRef = React.useRef<HTMLElement>(null); // for DOM measurements

  const currentItem = useMemo(
    () => getCurrentMenuItem(items, activeAnchor, currentUrl),
    [items, activeAnchor, currentUrl],
  );

  const {
    isScrollable,
    updateScrollState,
    handleOnScroll,
    handleScrollForward,
    handleScrollBackward,
    isScrollBackwardButtonVisible,
    isScrollForwardButtonVisible,
  } = useScroll(menuContainerRef);

  const focusOnMenuItem = useCallback(
    (menuItem: HTMLElement) => {
      const { current: menuContainer } = menuContainerRef;

      if (!isScrollable || !menuItem || !menuContainer) {
        return;
      }
      const rootMenuItem = menuContainer.closest<HTMLElement>(
        rootLevelMenuItemSelector,
      );
      if (rootMenuItem && rootMenuItem !== menuItem) {
        scrollToMenuItem(menuContainer, rootMenuItem);
      } else {
        scrollToMenuItem(menuContainer, menuItem);
      }
    },
    [isScrollable],
  );

  const handleOnFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const targetMenuItem = event.target;
      focusOnMenuItem(targetMenuItem);
    },
    [focusOnMenuItem],
  );

  useResizeObserver({
    ref: menuContainerRef,
    callback: updateScrollState,
  });
  useEffect(updateScrollState, [items, updateScrollState]);

  return (
    <div id={id} className={className}>
      <nav
        className={rootClassName}
        ref={menuContainerRef}
        {...(isScrollable && {
          onScroll: handleOnScroll,
          onFocus: handleOnFocus,
        })}
        data-part={MenuParts.Navbar}
        data-hook={DataHooks.menuRoot}
        {...a11yProps}
        aria-label={navAriaLabel || translations.menuNavAriaLabel}
        dir={direction}
      >
        <ul className={classes.container}>
          {items.map(item => (
            <MenuItem
              translations={translations}
              currentItem={currentItem}
              item={item}
              className={itemClassName}
              dropdownItemClassName={dropdownItemClassName}
              dropdownSubItemClassName={dropdownSubItemClassName}
              key={item.id}
              partToPreviewStateMap={partToPreviewStateMap}
              onItemClick={onItemClick}
              focusOnMenuItem={focusOnMenuItem}
              onItemMouseIn={onItemMouseIn}
              onItemMouseOut={onItemMouseOut}
              onItemDblClick={onItemDblClick}
              getAnimationPackage={getAnimationPackage}
              HorizontalDropdown={HorizontalDropdown}
              VerticalDropdown={VerticalDropdown}
              submenuDirection={submenuDirection}
              layoutClassType={layoutClassType}
            />
          ))}
        </ul>
        <ScrollControls
          scrollPageForward={handleScrollForward}
          scrollPageToBackward={handleScrollBackward}
          isScrollBackwardButtonShown={isScrollBackwardButtonVisible}
          isScrollForwardButtonShown={isScrollForwardButtonVisible}
          previewState={partToPreviewStateMap?.['scroll-button']}
          layoutClassType={layoutClassType}
        />
      </nav>
    </div>
  );
};

export default MenuContent;
